/**
 * Shared back-navigation helpers.
 *
 * Convention:
 * - List pages sync filters/tabs to URL search params (prefer replace: true for filter edits).
 * - When opening a detail page, pass returnTo (current path + query) via location.state
 *   and optionally as a ?returnTo= query param for refresh-safe back.
 * - Detail pages use useSmartBack({ fallback }) for Header onBack.
 * - smartBack uses replace: true so back does not leave a ping-pong history stack.
 */

/** Strip returnTo from a search string to avoid nested returnTo loops. */
export function stripReturnToFromSearch(search = '') {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) return '';
  const params = new URLSearchParams(raw);
  params.delete('returnTo');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Build a path+query string suitable for returnTo. */
export function buildReturnTo(pathname, search = '') {
  const path = pathname || '/';
  const cleaned = stripReturnToFromSearch(search || '');
  if (!cleaned) return path;
  return `${path}${cleaned}`;
}

/** Current location as returnTo (without nesting an existing returnTo param). */
export function buildReturnToFromLocation(location) {
  if (!location) return '/';
  return buildReturnTo(location.pathname, location.search || '');
}

/**
 * Resolve returnTo from location.state or ?returnTo= query.
 * Only allows same-origin relative paths starting with /.
 */
export function resolveReturnTo(location) {
  const fromState = location?.state?.returnTo;
  if (isSafeReturnPath(fromState)) return fromState;

  try {
    const params = new URLSearchParams(location?.search || '');
    const fromQuery = params.get('returnTo');
    // URLSearchParams.get already decodes; do not decodeURIComponent again
    if (isSafeReturnPath(fromQuery)) return fromQuery;
  } catch {
    // ignore malformed query
  }

  return null;
}

export function isSafeReturnPath(value) {
  if (typeof value !== 'string' || !value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  if (value.includes('://')) return false;
  return true;
}

/**
 * True when returnTo would send the user back to the same page they're already on
 * (path match), which creates an on-page / history loop.
 */
export function isReturnToCurrentLocation(returnTo, location) {
  if (!returnTo || !location?.pathname) return false;
  try {
    const targetPath = String(returnTo).split('?')[0].replace(/\/$/, '') || '/';
    const currentPath = String(location.pathname).replace(/\/$/, '') || '/';
    return targetPath === currentPath;
  } catch {
    return false;
  }
}

/**
 * Navigate to a detail route while preserving where to return.
 * @param {Function} navigate - react-router navigate
 * @param {string} to - detail path (may include query)
 * @param {{ returnTo?: string, state?: object, replace?: boolean }} options
 */
export function navigateWithReturn(navigate, to, options = {}) {
  const { returnTo, state = {}, replace = false } = options;
  const cleanReturnTo =
    returnTo && isSafeReturnPath(returnTo)
      ? buildReturnTo(
          returnTo.split('?')[0],
          returnTo.includes('?') ? returnTo.slice(returnTo.indexOf('?')) : ''
        )
      : null;
  const nextState = {
    ...state,
    ...(cleanReturnTo ? { returnTo: cleanReturnTo } : {})
  };

  let target = to;
  if (cleanReturnTo) {
    const [pathPart, existingQuery = ''] = String(to).split('?');
    const params = new URLSearchParams(existingQuery);
    if (!params.has('returnTo')) {
      params.set('returnTo', cleanReturnTo);
    }
    const qs = params.toString();
    target = qs ? `${pathPart}?${qs}` : pathPart;
  }

  navigate(target, { state: nextState, replace });
}

/**
 * Perform smart back: returnTo → fallback → history -1 → fallback or '/'.
 * Uses replace: true for absolute targets so Company ↔ Projects cannot ping-pong.
 */
export function smartBack(navigate, location, { fallback = '/' } = {}) {
  const returnTo = resolveReturnTo(location);
  if (returnTo && !isReturnToCurrentLocation(returnTo, location)) {
    navigate(returnTo, { replace: true });
    return;
  }
  if (fallback && !isReturnToCurrentLocation(fallback, location)) {
    navigate(fallback, { replace: true });
    return;
  }
  if (typeof window !== 'undefined' && window.history.length > 1) {
    navigate(-1);
    return;
  }
  navigate(fallback || '/', { replace: true });
}
